import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Modal,
  message,
  Card,
  Input,
  Select,
  Form,
  Row,
  Col,
  DatePicker,
} from 'antd';
import {
  UserOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { getUsers, deleteUser, getUsersMock } from '../services/userService';
import { User } from '../types';
import UserForm from '../components/users/UserForm';
import UserDetail from '../components/users/UserDetail';
import ErrorBoundary from '../components/ErrorBoundary';

const { Option } = Select;

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isDetailVisible, setIsDetailVisible] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
    phoneVerified: '',
    isAppUser: '',
    hasUpdatedInfo: '',
    userSource: '',
    createdAtFrom: '',
    createdAtTo: '',
  });

  // Load users
  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      // Build filter object for API
      const filterObj: any = {};
      
      if (filters.phoneVerified !== '') {
        filterObj.phoneVerified = filters.phoneVerified;
      }
      
      if (filters.userSource !== '') {
        filterObj.userSource = filters.userSource;
      }
      
      if (filters.hasUpdatedInfo !== '') {
        filterObj.hasUpdatedInfo = filters.hasUpdatedInfo;
      }
      
      const apiFilters = {
        search: filters.search,
        filter: Object.keys(filterObj).length > 0 ? filterObj : undefined,
        createdAtFrom: filters.createdAtFrom || undefined,
        createdAtTo: filters.createdAtTo || undefined,
      };
      
      const response = await getUsers(
        pagination.current,
        pagination.pageSize,
        apiFilters
      );
      
      // Ensure users data is properly formatted based on new API structure
      const sanitizedUsers = (response.data || []).map((user: any) => ({
        ...user,
        id: user.id || 'unknown',
        username: user.name || 'Unknown User', // Map 'name' to 'username' for display
        name: user.name || 'Unknown User',
        email: user.email || 'No Email',
        phone: user.phone || 'No Phone',
        status: user.phoneVerified ? 'active' : 'unverified', // Map phoneVerified to status
        role: user.isAppUser ? 'app_user' : 'web_user', // Map isAppUser to role
        energy: user.energy ?? 0,
        phoneVerified: user.phoneVerified ?? false,
        isAppUser: user.isAppUser ?? false,
        province: user.province || null,
        ward: user.ward || null,
        gender: user.gender || null,
        birthYear: user.birthYear || null,
        refBy: user.refBy || null,
        lastLoginAt: user.lastLoginAt || null,
        createdAt: user.createdAt || new Date().toISOString(),
        updatedAt: user.updatedAt || new Date().toISOString(),
      }));
      
      setUsers(sanitizedUsers);
      setPagination(prev => ({
        ...prev,
        total: response.total || 0,
        current: response.current || 1,
      }));
    } catch (error) {
      console.error('Failed to load users:', error);
      message.error('Failed to load users');
      
      // Fallback to mock data
      const mockData = getUsersMock();
      setUsers(mockData.data);
      setPagination(prev => ({
        ...prev,
        total: mockData.total,
      }));
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, filters]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Handle create user
  const handleCreate = () => {
    setSelectedUser(null);
    setModalMode('create');
    setIsModalVisible(true);
  };

  // Handle edit user
  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setModalMode('edit');
    setIsModalVisible(true);
  };

  // Handle view user details
  const handleViewDetails = (user: User) => {
    setSelectedUser(user);
    setIsDetailVisible(true);
  };

  // Handle delete user
  const handleDelete = (user: User) => {
    setUserToDelete(user);
    setDeleteModalVisible(true);
  };

  // Confirm delete user
  const confirmDelete = async () => {
    if (!userToDelete) return;

    try {
      await deleteUser(userToDelete.id);
      message.success('User deleted successfully');
      setDeleteModalVisible(false);
      setUserToDelete(null);
      loadUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      message.error('Failed to delete user');
    }
  };

  // Handle form success
  const handleFormSuccess = () => {
    setIsModalVisible(false);
    setSelectedUser(null);
    loadUsers();
  };

  // Handle table change (pagination, filters, sorting)
  const handleTableChange = (newPagination: any) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
  };

  // Handle search
  const handleSearch = (values: any) => {
    // Format dates to YYYY-MM-DD if they exist
    const formattedValues = {
      ...values,
      createdAtFrom: values.createdAtFrom ? values.createdAtFrom.format('YYYY-MM-DD') : '',
      createdAtTo: values.createdAtTo ? values.createdAtTo.format('YYYY-MM-DD') : '',
    };
    setFilters(formattedValues);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({ search: '', phoneVerified: '', isAppUser: '', hasUpdatedInfo: '', userSource: '', createdAtFrom: '', createdAtTo: '' });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Table columns
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id: string) => id.slice(0, 8) + '...',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone: string) => phone || 'N/A',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email: string) => email || 'N/A',
    },
    {
      title: 'Phone Verified',
      dataIndex: 'phoneVerified',
      key: 'phoneVerified',
      render: (verified: boolean) => {
        const color = verified ? 'green' : 'orange';
        const icon = verified ? '✅' : '⚠️';
        return <Tag color={color}>{icon} {verified ? 'VERIFIED' : 'UNVERIFIED'}</Tag>;
      },
      filters: [
        { text: 'Verified', value: true },
        { text: 'Unverified', value: false },
      ],
    },
    {
      title: 'User Type',
      dataIndex: 'isAppUser',
      key: 'isAppUser',
      render: (isAppUser: boolean) => {
        const color = isAppUser ? 'blue' : 'gray';
        const icon = isAppUser ? '📱' : '🌐';
        return <Tag color={color}>{icon} {isAppUser ? 'APP USER' : 'WEB USER'}</Tag>;
      },
      filters: [
        { text: 'App User', value: true },
        { text: 'Web User', value: false },
      ],
    },
    {
      title: 'Profile Status',
      key: 'profileStatus',
      render: (_: any, record: User) => {
        const hasUpdatedInfo = record.province || record.ward || record.gender || record.birthYear;
        const icon = hasUpdatedInfo ? '✅' : '⚠️';
        return (
          <Tag color={hasUpdatedInfo ? 'green' : 'orange'}>
            {icon} {hasUpdatedInfo ? 'UPDATED' : 'INCOMPLETE'}
          </Tag>
        );
      },
      filters: [
        { text: 'Updated Info', value: true },
        { text: 'Incomplete Info', value: false },
      ],
    },
    {
      title: 'Energy',
      dataIndex: 'energy',
      key: 'energy',
      render: (energy: number) => {
        return energy !== undefined && energy !== null ? energy : 0;
      },
      sorter: true,
    },
    {
      title: 'Province',
      dataIndex: 'province',
      key: 'province',
      render: (province: string) => province || 'N/A',
    },
    {
      title: 'Gender',
      dataIndex: 'gender',
      key: 'gender',
      render: (gender: string) => {
        if (!gender) return 'N/A';
        const color = gender === 'male' ? 'blue' : gender === 'female' ? 'pink' : 'gray';
        return <Tag color={color}>{gender.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Birth Year',
      dataIndex: 'birthYear',
      key: 'birthYear',
      render: (birthYear: number) => birthYear || 'N/A',
    },
    {
      title: 'Last Login',
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      render: (date: string) => {
        return date ? new Date(date).toLocaleDateString() : 'Never';
      },
      sorter: true,
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => {
        return date ? new Date(date).toLocaleDateString() : 'N/A';
      },
      sorter: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: User) => (
        <Space size="small">
          <Button
            icon={<UserOutlined />}
            size="small"
            onClick={() => handleViewDetails(record)}
            title="View Details"
          />
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
            title="Edit User"
          />
          <Button
            icon={<DeleteOutlined />}
            size="small"
            danger
            onClick={() => handleDelete(record)}
            title="Delete User"
          />
        </Space>
      ),
    },
  ];

  return (
    <ErrorBoundary>
      <div>
        <Card title="User Management">
          {/* Search and Filters */}
          <Form
            layout="inline"
            onFinish={handleSearch}
            style={{ marginBottom: 16 }}
          >
            <Row gutter={16} style={{ width: '100%' }}>
              <Col span={6}>
                <Form.Item name="search" label="Search">
                  <Input
                    placeholder="Search by name or phone"
                    prefix={<SearchOutlined />}
                  />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item name="phoneVerified" label="Phone Verified">
                  <Select placeholder="All" allowClear>
                    <Option value={true}>Verified</Option>
                    <Option value={false}>Unverified</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item name="userSource" label="User Source">
                  <Select placeholder="All Sources" allowClear>
                    <Option value="app">App Users</Option>
                    <Option value="web">Web Users</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item name="hasUpdatedInfo" label="Profile Status">
                  <Select placeholder="All" allowClear>
                    <Option value={true}>Updated Info</Option>
                    <Option value={false}>Not Updated</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item>
                  <Space>
                    <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                      Search
                    </Button>
                    <Button onClick={resetFilters} icon={<ReloadOutlined />}>
                      Reset
                    </Button>
                    <Button type="primary" onClick={handleCreate} icon={<PlusOutlined />}>
                      Add User
                    </Button>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16} style={{ width: '100%', marginTop: 16 }}>
              <Col span={6}>
                <Form.Item name="createdAtFrom" label="Created From">
                  <DatePicker
                    placeholder="Select start date"
                    style={{ width: '100%' }}
                    format="YYYY-MM-DD"
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="createdAtTo" label="Created To">
                  <DatePicker
                    placeholder="Select end date"
                    style={{ width: '100%' }}
                    format="YYYY-MM-DD"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>

          {/* Users Table */}
          <Table
            columns={columns}
            dataSource={users}
            rowKey="id"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} users`,
            }}
            onChange={handleTableChange}
          />
        </Card>

        {/* Create/Edit User Modal */}
        <Modal
          title={modalMode === 'create' ? 'Create User' : 'Edit User'}
          open={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={null}
          width={600}
        >
          <UserForm
            user={selectedUser}
            mode={modalMode}
            onSuccess={handleFormSuccess}
            onCancel={() => setIsModalVisible(false)}
          />
        </Modal>

        {/* User Detail Modal */}
        <Modal
          title="User Details"
          open={isDetailVisible}
          onCancel={() => setIsDetailVisible(false)}
          footer={[
            <Button key="close" onClick={() => setIsDetailVisible(false)}>
              Close
            </Button>,
          ]}
          width={800}
        >
          {selectedUser && <UserDetail user={selectedUser} />}
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          title="Delete User"
          open={deleteModalVisible}
          onOk={confirmDelete}
          onCancel={() => setDeleteModalVisible(false)}
          okText="Delete"
          okButtonProps={{ danger: true }}
        >
          <p>
            Are you sure you want to delete user{' '}
            <strong>{userToDelete?.username}</strong>?
          </p>
          <p>This action cannot be undone.</p>
        </Modal>
      </div>
    </ErrorBoundary>
  );
};

export default UsersPage;
